import { Check } from 'lucide-react';

import { InvoiceRunType } from '@/graphql/generatedTypes';
import { cn } from '@/lib/utils';
import {
  runTypeLabel,
  type RunPresentationState,
} from './invoiceRunPresentation';

/**
 * Pill + status-dot vocabulary for invoice runs. Colors map the handoff's
 * tokens onto the app palette: brand-green → emerald, status-info → sky,
 * status-warning → amber, plus the theme's destructive/primary/muted vars.
 */

const pillBase =
  'tw:inline-flex tw:h-5 tw:shrink-0 tw:items-center tw:gap-1 tw:rounded-full tw:px-2 tw:text-xs tw:font-medium tw:whitespace-nowrap';

/** Spanish / Default (muted) · Complete & charge (dark primary). */
export function RunTypePill({ type }: { type: InvoiceRunType }) {
  return (
    <span
      className={cn(
        pillBase,
        type === InvoiceRunType.CompleteAndCharge
          ? 'tw:bg-primary tw:text-primary-foreground'
          : 'tw:bg-muted tw:text-muted-foreground',
      )}
    >
      {runTypeLabel(type)}
    </span>
  );
}

/** Dashed pill marking dry runs everywhere they appear. */
export function DryRunPill({ long = false }: { long?: boolean }) {
  return (
    <span
      className={cn(
        pillBase,
        'tw:border tw:border-dashed tw:border-muted-foreground/60 tw:text-muted-foreground',
      )}
    >
      {long ? 'Dry run — cannot be charged' : 'Dry'}
    </span>
  );
}

export function ChargedPill() {
  return (
    <span
      className={cn(
        pillBase,
        'tw:bg-emerald-50 tw:text-emerald-700 tw:border tw:border-emerald-200 tw:dark:bg-emerald-950 tw:dark:text-emerald-400 tw:dark:border-emerald-900',
      )}
    >
      <Check className="tw:size-3" />
      Charged
    </span>
  );
}

const STATE_LABEL: Record<RunPresentationState, string> = {
  scheduled: 'Scheduled',
  starting: 'Starting',
  running: 'Running',
  // Stalled runs keep the label "Running"; only the color warns.
  stalled: 'Running',
  done: 'Done',
  failed: 'Failed',
};

const STATE_DOT: Record<RunPresentationState, string> = {
  scheduled: 'tw:bg-muted-foreground/50',
  starting: 'tw:bg-amber-500',
  running: 'tw:bg-sky-600 tw:animate-pulse',
  stalled: 'tw:bg-amber-500 tw:animate-pulse',
  done: 'tw:bg-emerald-500',
  failed: 'tw:bg-destructive',
};

export function StatusDot({ state }: { state: RunPresentationState }) {
  return (
    <span
      className={cn(
        'tw:inline-block tw:size-2 tw:shrink-0 tw:rounded-full',
        STATE_DOT[state],
      )}
    />
  );
}

/** Dot + label, used in the history table and the detail header. */
export function RunStatusLabel({ state }: { state: RunPresentationState }) {
  return (
    <span className="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-foreground">
      <StatusDot state={state} />
      {STATE_LABEL[state]}
    </span>
  );
}
